// Formulários do técnico: envio pelo card, respostas com fotos, validação e construtor (admin).
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Avatar, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  FormControlLabel, Grid, IconButton, InputAdornment, List, ListItem, ListItemAvatar, ListItemText, Menu, MenuItem,
  Paper, Radio, RadioGroup, Switch, Tab, Tabs, TextField, Tooltip, Typography, makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import LinkIcon from "@material-ui/icons/Link";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import GetAppIcon from "@material-ui/icons/GetApp";

import Galeria, { tipoMidia } from "./Galeria";
import MainContainer from "../components/MainContainer";
import MainHeader from "../components/MainHeader";
import MainHeaderButtonsWrapper from "../components/MainHeaderButtonsWrapper";
import Title from "../components/Title";
import { AuthContext } from "../context/Auth/AuthContext";
import suporteApi, { baixar, mensagemErro } from "./api";
import { usePermissao } from "./permissoes";
import { DialogCard, PainelCard, copiar, dataBR, haQuanto } from "./comum";

const useStyles = makeStyles((theme) => ({
  resposta: { padding: "4px 0", fontSize: 13 },
  pergunta: { color: theme.palette.text.secondary, fontSize: 12 },
  // visualização das respostas: um cartão por pergunta
  cartaoResp: {
    display: "flex", gap: 12, alignItems: "flex-start", border: `1px solid ${theme.palette.divider}`, borderRadius: 12,
    padding: "10px 14px", marginBottom: 8, background: theme.palette.background.paper,
  },
  numero: {
    flex: "none", width: 30, height: 30, borderRadius: "50%", border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12.5, fontWeight: 700, marginTop: 2,
  },
  perguntaResp: { color: theme.palette.text.secondary, fontSize: 12.5, marginBottom: 2 },
  valorResp: { fontSize: 14.5, fontWeight: 500, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  semResp: { fontSize: 13, fontStyle: "italic", color: theme.palette.text.disabled },
  secaoResp: {
    display: "flex", alignItems: "center", gap: 8, margin: "14px 0 8px", fontWeight: 700, fontSize: 13.5,
    color: theme.palette.primary.main, textTransform: "uppercase", letterSpacing: ".02em",
    "&::after": { content: '""', flex: 1, height: 1, background: theme.palette.divider },
  },
  resumoResp: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12.5, color: theme.palette.text.secondary, marginBottom: 10 },
  secao: { fontWeight: 700, color: theme.palette.primary.main, marginTop: 8, fontSize: 13 },
  fotos: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 },
  foto: { width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: `1px solid ${theme.palette.divider}` },
  envio: { border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: 10, margin: "8px 0" },
  papel: { padding: 16, overflowY: "auto", flex: 1, ...theme.scrollbarStyles },
  pergEdit: { border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: 10, marginBottom: 10 },
  // lista e ficha (mesmo estilo da Base clientes)
  filtros: { padding: "10px 14px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 },
  busca: { flex: "1 1 320px" },
  lista: { flex: 1, overflowY: "auto", ...theme.scrollbarStyles },
  item: { borderBottom: `1px solid ${theme.palette.divider}` },
  fraco: { color: theme.palette.text.secondary, fontSize: 12.5 },
  lado: { textAlign: "right", minWidth: 190, marginLeft: 12, [theme.breakpoints.down("xs")]: { display: "none" } },
  ficha: { width: 860, maxWidth: "100vw", display: "flex", flexDirection: "column", height: "100%" },
  fichaTopo: { padding: "10px 16px 0", borderBottom: `1px solid ${theme.palette.divider}` },
  fichaCorpo: { flex: 1, overflowY: "auto", padding: 16, ...theme.scrollbarStyles },
  // editor de modelos (tela cheia)
  editorTopo: {
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "8px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper,
  },
  editorCorpo: { flex: 1, overflowY: "auto", padding: 16, background: theme.palette.background.default, ...theme.scrollbarStyles },
}));

export const STATUS_FORM = {
  ENVIADO: ["Aguardando técnico", "default"],
  RESPONDIDO: ["Para validar", "secondary"],
  CORRECAO: ["Correção pedida", "default"],
  VALIDADO: ["Validado", "primary"],
  EXPIRADO: ["Expirado", "default"],  // aguardando técnico ou correção com o link vencido (Reenviar link volta)
};
// situação mostrada: "Expirado" quando o link venceu sem o técnico responder
export const situacao = (e) => (e?.expirado ? "EXPIRADO" : e?.status);

const TIPOS = [
  ["texto", "Texto curto"], ["paragrafo", "Parágrafo"], ["escolha", "Múltipla escolha"],
  ["caixas", "Caixas de seleção"], ["lista", "Lista suspensa"], ["data", "Data"], ["hora", "Hora"],
  ["numero", "Número"], ["iccid", "ICCID do chip (confere o número)"], ["foto", "Anexo (foto, documento ou vídeo)"],
  ["senha", "Senha numérica (oculta, cifrada)"], ["telefone", "Telefone (DDD + número, sem 0)"],
  ["tabela", "Tabela (o técnico adiciona linhas, ex.: zonas)"],
  ["assinatura", "Assinatura (desenhada na tela)"],
  ["secao", "Título de seção"],
];
// tipos do checklist da supervisão: a verificação (conforme / não conforme) e os tipos simples do formulário
export const TIPOS_CHECKLIST = [
  ["verificacao", "Verificação (conforme / não conforme)"], ["texto", "Texto curto"], ["paragrafo", "Parágrafo"],
  ["escolha", "Múltipla escolha"], ["caixas", "Caixas de seleção"], ["lista", "Lista suspensa"], ["data", "Data"],
  ["hora", "Hora"], ["numero", "Número"], ["foto", "Fotos"], ["assinatura", "Assinatura (desenhada na tela)"],
  ["secao", "Título de seção"],
];
export const OPCOES_VERIFICACAO = ["Conforme", "Não conforme", "Não se aplica", "Não verificado"];
const opcoesDe = (q) => (q.tipo === "verificacao" ? OPCOES_VERIFICACAO : q.opcoes || []);
const MAPEAR = [["", "Não preenche o card"], ["qra", "QRA do técnico"], ["os", "O.S"],
  ["filial", "Nome da loja (só vem preenchido do card)"],
  ["zoneamento", "Cliente: zoneamento (tabela)"], ["cliente_modelo", "Cliente: modelo da central"],
  ["chip_qtd", "Cliente: quantidade de chips"], ["chip_iccid1", "Cliente: ICCID chip 1"], ["chip_iccid2", "Cliente: ICCID chip 2"], ["conta", "Conta"], ["tipo", "Tipo de serviço"], ["sistema", "Sistema"]];

// ------------------------------------------------------------------ respostas + validação
// senha guardada cifrada: quem vê o formulário revela (fica registrado quem viu)
const SenhaOculta = ({ envio, pergunta }) => {
  const podeRevelar = usePermissao("formularios.revelar_senha");
  const [senha, setSenha] = useState(null);
  const revelar = async () => {
    try {
      const { data } = await suporteApi.post(`/api/formularios/envios/${envio.id}/senha/${pergunta.id}`);
      setSenha(data.senha);
      setTimeout(() => setSenha(null), 30000);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  if (!(envio.respostas || {})[pergunta.id]) return "—";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: 2 }}>{senha || "••••"}</span>
      {!senha && podeRevelar && (
        <Button size="small" variant="outlined" onClick={revelar}>Revelar senha</Button>
      )}
      {senha && <Typography variant="caption" color="textSecondary">(some em 30 s · visualização registrada)</Typography>}
    </span>
  );
};

export const rotuloStatus = (e, status = e.status) => (e?.rotulos || {})[status] || (STATUS_FORM[status] || [status])[0];

// um cartão por pergunta (fora do componente: não remonta a cada digitação)
const CartaoResp = ({ c, numero, p, children }) => (
  <div className={c.cartaoResp}>
    <div className={c.numero}>{numero}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className={c.perguntaResp}>{p.titulo}{p.mapear ? ` · preenche ${p.mapear.replace(/^card:/, "").toUpperCase()} no card` : ""}</div>
      {children}
    </div>
  </div>
);

// sugestões de observação para pedir correção: o que ficou sem resposta + motivos comuns
const sugestoesCorrecao = (envio) => {
  const r = envio.respostas || {};
  const arquivos = envio.urls_arquivos || {};
  const faltando = (envio.perguntas || []).filter((p) => p.tipo !== "secao" && !(p.tipo === "senha" && p.confirma))
    .filter((p) => (p.tipo === "foto" ? !(arquivos[p.id] || []).length : !(Array.isArray(r[p.id]) ? r[p.id].length : r[p.id])))
    .map((p) => `Pendente: ${p.titulo}`);
  return [...faltando, "Foto ilegível / sem foco", "Número informado não confere", "Informação incompleta"];
};

export const RespostasEnvio = ({ envio, onMudou }) => {
  const podeValidar = usePermissao("formularios.validar");
  const c = useStyles();
  const [comentario, setComentario] = useState("");
  const [faltaComentario, setFaltaComentario] = useState(false);
  const campoComentario = useRef(null);
  // documentos com validade: a data informada pelo técnico vem preenchida e o analista confere
  const [validades, setValidades] = useState(() => Object.fromEntries((envio.perguntas || [])
    .filter((p) => p.tipo === "foto" && p.validade).map((p) => [p.id, (envio.respostas || {})[`${p.id}__validade`] || ""])));
  const [avisar, setAvisar] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const validar = async (aprovado) => {
    setOcupado(true);
    try {
      const { data } = await suporteApi.post(`/api/formularios/envios/${envio.id}/validar`, { aprovado, comentario, avisar, validades });
      toast.success(aprovado ? rotuloStatus(envio, "VALIDADO") : "Correção pedida");
      setComentario("");
      setFaltaComentario(false);
      onMudou && onMudou(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setOcupado(false);
  };
  const r = envio.respostas || {};
  // numeração das perguntas (sem seções e sem a confirmação de senha)
  const numeros = {};
  let n = 0;
  (envio.perguntas || []).forEach((p) => {
    if (p.tipo !== "secao" && !(p.tipo === "senha" && p.confirma)) numeros[p.id] = ++n;
  });
  const respondidas = (envio.perguntas || []).filter((p) => numeros[p.id] && (Array.isArray(r[p.id]) ? r[p.id].length : r[p.id])).length;
  const midias = Object.values(envio.urls_arquivos || {}).flat();
  const qtd = (t) => midias.filter((u) => tipoMidia(u) === t).length;
  return (
    <div>
      <div className={c.resumoResp}>
        <span>{`📝 ${respondidas} de ${n} respondidas`}</span>
        {qtd("imagem") > 0 && <span>{`🖼️ ${qtd("imagem")} foto(s)`}</span>}
        {qtd("video") > 0 && <span>{`🎥 ${qtd("video")} vídeo(s)`}</span>}
        {qtd("pdf") + qtd("arquivo") > 0 && <span>{`📄 ${qtd("pdf") + qtd("arquivo")} documento(s)`}</span>}
      </div>
      {envio.perguntas.map((p) => {
        if (p.tipo === "secao") return <div key={p.id} className={c.secaoResp}>{p.titulo}</div>;
        const v = r[p.id];
        let valor;
        if (p.tipo === "tabela") {
          const linhas = Array.isArray(v) ? v : [];
          const url = (n) => `${(envio.link || "").replace(/\/f\/.*$/, "")}/f/${encodeURIComponent((envio.link || "").split("/f/")[1] || "")}/arquivos/${encodeURIComponent(n)}`;
          return (
            <div key={p.id} className={c.cartaoResp}>
              <div className={c.numero}>{numeros[p.id]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
              <div className={c.perguntaResp}>{`${p.titulo} (${linhas.length})`}{p.mapear ? ` · grava ${p.mapear === "zoneamento" ? "o zoneamento" : p.mapear} no cliente` : ""}</div>
              <div style={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>{(p.colunas || []).map((col) => <TableCell key={col.id}>{col.titulo}</TableCell>)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {linhas.map((l, i) => (
                      <TableRow key={i}>
                        {(p.colunas || []).map((col) => (
                          <TableCell key={col.id}>
                            {col.tipo === "foto" ? (
                              <Galeria itens={(l[col.id] || []).map((n) => ({ url: url(n), titulo: `${p.titulo} · linha ${i + 1} · ${col.titulo}` }))} />
                            ) : l[col.id] || "—"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </div>
            </div>
          );
        }
        if (p.tipo === "senha") {
          if (p.confirma) return null;  // a confirmação não é guardada
          valor = <SenhaOculta envio={envio} pergunta={p} />;
        } else if (p.tipo === "foto" || p.tipo === "assinatura") {
          const urls = (envio.urls_arquivos || {})[p.id] || [];
          valor = urls.length ? <Galeria itens={urls.map((u) => ({ url: u, titulo: p.titulo }))} /> : null;
        } else valor = Array.isArray(v) ? v.join(", ") || null : v || null;
        const comValidade = p.tipo === "foto" && p.validade && ((envio.arquivos || {})[p.id] || []).length > 0;
        return (
          <CartaoResp key={p.id} c={c} numero={numeros[p.id]} p={p}>
            {valor ? <div className={c.valorResp}>{valor}</div> : <div className={c.semResp}>Não respondida</div>}
            {comValidade && (envio.status === "RESPONDIDO" ? (
              <TextField type="date" size="small" variant="outlined" margin="dense" label="Validade (confira) *"
                InputLabelProps={{ shrink: true }} value={validades[p.id] || ""}
                error={!validades[p.id]} helperText={!validades[p.id] ? "Obrigatório para validar" : `vai para o controle de documentos como ${p.documento || p.titulo}`}
                onChange={(e) => setValidades({ ...validades, [p.id]: e.target.value })} />
            ) : (envio.respostas || {})[`${p.id}__validade`] && (
              <div style={{ fontSize: 12.5 }}>{`Validade: ${dataBR((envio.respostas || {})[`${p.id}__validade`], true)}`}</div>
            ))}
          </CartaoResp>
        );
      })}
      {envio.status === "RESPONDIDO" && podeValidar && (
        <>
          <TextField fullWidth size="small" variant="outlined" margin="dense" multiline inputRef={campoComentario}
            label="Observação (vai para o técnico ao pedir correção)" value={comentario} error={faltaComentario && !comentario.trim()}
            helperText={faltaComentario && !comentario.trim() ? "Escreva ou escolha abaixo o que precisa ser corrigido" : ""}
            onChange={(e) => setComentario(e.target.value)} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "2px 0 6px" }}>
            {sugestoesCorrecao(envio).map((t) => (
              <Chip key={t} size="small" variant="outlined" clickable label={t}
                onClick={() => setComentario((atual) => (atual.trim() ? `${atual.trim()}\n${t}` : t))} />
            ))}
          </div>
          {envio.whatsapp_numero && (
            <FormControlLabel
              control={<Checkbox size="small" color="primary" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} />}
              label={`Avisar o técnico pelo WhatsApp ao validar (${envio.whatsapp_numero}). Na correção ele é sempre avisado.`}
            />
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <Button variant="contained" color="primary" size="small" disabled={ocupado} onClick={() => validar(true)}>
              {(envio.rotulos || {}).botao_validar || "Validar"}
            </Button>
            <Button variant="outlined" size="small" disabled={ocupado}
              onClick={() => {
                if (!comentario.trim()) { setFaltaComentario(true); campoComentario.current?.focus(); return; }
                validar(false);
              }}>
              Pedir correção (reenvia o link)
            </Button>
          </div>
        </>
      )}
      {envio.comentario && envio.status !== "RESPONDIDO" && (
        <Typography variant="caption" color="textSecondary">{`Comentário: ${envio.comentario}`}</Typography>
      )}
    </div>
  );
};

const CabecalhoEnvio = ({ envio }) => {
  const [rot, cor] = STATUS_FORM[situacao(envio)] || [envio.status, "default"];
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Typography variant="body2"><b>{envio.nome_formulario}</b></Typography>
      <Chip size="small" label={rot} color={cor} />
      <Typography variant="caption" color="textSecondary" style={{ width: "100%" }}>
        {`Enviado por ${envio.enviado_por || "-"} em ${dataBR(envio.enviado_em)}`}
        {envio.respondido_em ? ` · respondido ${dataBR(envio.respondido_em)}` : ""}
        {envio.validado_em ? ` · ${envio.status === "VALIDADO" ? "validado" : "revisado"} por ${envio.validado_por} ${dataBR(envio.validado_em)}` : ""}
      </Typography>
    </div>
  );
};

// ------------------------------------------------------------------ seção no card
// validade do link único: "expira em 3 h" / "expirado"
const ChipValidade = ({ expira }) => {
  const min = Math.round((new Date(expira) - new Date()) / 60000);
  if (min <= 0) return <Chip size="small" label="link expirado" style={{ background: "#f44336", color: "#fff", height: 20, fontSize: 11 }} />;
  const txt = min >= 120 ? `${Math.floor(min / 60)} h` : `${min} min`;
  return <Chip size="small" variant="outlined" label={`expira em ${txt}`} style={{ height: 20, fontSize: 11, alignSelf: "center" }} />;
};

export const FormulariosDoCard = ({ card, onCardMudou }) => {
  const podeReenviar = usePermissao("formularios.reenviar");
  const c = useStyles();
  const [envios, setEnvios] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [abrindo, setAbrindo] = useState(false);
  const [escolhido, setEscolhido] = useState("");
  const [whats, setWhats] = useState(true);
  const [aberto, setAberto] = useState(null);

  const carregar = useCallback(() => {
    suporteApi.get(`/api/atendimentos/${card.id}/formularios`).then((r) => setEnvios(r.data)).catch(() => {});
  }, [card.id]);
  useEffect(() => {
    carregar();
  }, [carregar, card.andamentos?.length]);

  const abrirEnvio = async () => {
    const { data } = await suporteApi.get("/api/formularios", { params: { somente_ativos: true } });
    setModelos(data);
    setEscolhido(data[0]?.id || "");
    setWhats(!!card.whatsapp_destino);
    setAbrindo(true);
  };
  const reenviar = async (e) => {
    if (!window.confirm(`Gerar um link novo para "${e.nome_formulario}" e mandar ao técnico? O link anterior deixa de funcionar.`)) return;
    try {
      await suporteApi.post(`/api/formularios/envios/${e.id}/reenviar`);
      toast.success("Link novo enviado");
      carregar();
      onCardMudou && onCardMudou();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const enviar = async (soCopiar = false) => {
    const mandar = whats && !soCopiar;
    try {
      const { data } = await suporteApi.post(`/api/atendimentos/${card.id}/formularios`, {
        formulario_id: escolhido, enviar_whatsapp: mandar,
      });
      if (mandar) toast.success("Formulário enviado ao técnico");
      else copiar(data.link);
      setAbrindo(false);
      carregar();
      onCardMudou && onCardMudou();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  return (
    <>
      <Divider style={{ margin: "10px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle2" color="textSecondary">FORMULÁRIOS</Typography>
        {card.status !== "FECHADO" && (
          <Button size="small" startIcon={<SendIcon />} onClick={() => abrirEnvio().catch((e) => toast.error(mensagemErro(e)))}>
            Enviar formulário
          </Button>
        )}
      </div>
      {envios.length === 0 && <Typography variant="body2" color="textSecondary">Nenhum formulário enviado.</Typography>}
      {envios.map((e) => (
        <div key={e.id} className={c.envio}>
          <CabecalhoEnvio envio={e} />
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            {e.status !== "ENVIADO" && (
              <Button size="small" color="primary" onClick={() => setAberto(aberto === e.id ? null : e.id)}>
                {aberto === e.id ? "Fechar respostas" : e.status === "RESPONDIDO" ? "Ver e validar" : "Ver respostas"}
              </Button>
            )}
            <Button size="small" startIcon={<FileCopyOutlined />} onClick={() => copiar(e.link)}>Copiar link</Button>
            {["ENVIADO", "CORRECAO", "RESPONDIDO"].includes(e.status) && card.status !== "FECHADO" && podeReenviar && (
              <Button size="small" startIcon={<SendIcon />} onClick={() => reenviar(e)}>Reenviar link</Button>
            )}
            {e.expira_em && e.status !== "VALIDADO" && <ChipValidade expira={e.expira_em} />}
          </div>
          {aberto === e.id && (
            <RespostasEnvio envio={e} onMudou={() => { carregar(); onCardMudou && onCardMudou(); }} />
          )}
        </div>
      ))}
      <Dialog open={abrindo} onClose={() => setAbrindo(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Enviar formulário ao técnico</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Formulário" value={escolhido} onChange={(e) => setEscolhido(e.target.value)}>
            {modelos.map((m) => <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>)}
          </TextField>
          {card.whatsapp_destino ? (
            <FormControlLabel
              control={<Checkbox color="primary" checked={whats} onChange={(e) => setWhats(e.target.checked)} />}
              label={`Mandar o link pelo WhatsApp (${card.whatsapp_destino})`}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Técnico sem WhatsApp no card: o link será copiado para você enviar.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrindo(false)}>Cancelar</Button>
          <Button startIcon={<FileCopyOutlined />} disabled={!escolhido} onClick={() => enviar(true)}>
            Copiar link
          </Button>
          {whats && card.whatsapp_destino && (
            <Button color="primary" variant="contained" disabled={!escolhido} onClick={() => enviar(false)}>
              Enviar pelo WhatsApp
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

// ------------------------------------------------------------------ construtor (admin)
// Colunas de uma pergunta "tabela" (cada linha que o técnico adiciona tem estas colunas)
const TIPOS_COLUNA = [["texto", "Texto"], ["numero", "Número"], ["lista", "Lista"], ["foto", "Foto"]];
const ColunasEditor = ({ colunas, onMudar }) => {
  const mudar = (i, d) => onMudar(colunas.map((c, j) => (j === i ? { ...c, ...d } : c)));
  return (
    <div style={{ background: "rgba(0,0,0,0.03)", borderRadius: 6, padding: 8 }}>
      <Typography variant="caption" color="textSecondary">Colunas de cada linha</Typography>
      {colunas.map((c, i) => (
        <Grid container spacing={1} key={c.id || i} alignItems="center" style={{ marginTop: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" variant="outlined" label={`Coluna ${i + 1}`} value={c.titulo || ""}
              onChange={(e) => mudar(i, { titulo: e.target.value })} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select fullWidth size="small" variant="outlined" label="Tipo" value={c.tipo || "texto"}
              onChange={(e) => mudar(i, { tipo: e.target.value })}>
              {TIPOS_COLUNA.map(([v, r]) => <MenuItem key={v} value={v}>{r}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            {c.tipo === "lista" ? (
              <TextField fullWidth size="small" variant="outlined" multiline label="Opções (uma por linha)"
                value={(c.opcoes || []).join("\n")} onChange={(e) => mudar(i, { opcoes: e.target.value.split("\n") })} />
            ) : (
              <FormControlLabel control={<Checkbox size="small" color="primary" checked={!!c.auto}
                onChange={(e) => mudar(i, { auto: e.target.checked })} />} label="Numerar sozinho (001, 002…)" />
            )}
          </Grid>
          <Grid item xs={10} sm={1}>
            <FormControlLabel control={<Checkbox size="small" color="primary" checked={!!c.obrigatoria}
              onChange={(e) => mudar(i, { obrigatoria: e.target.checked })} />} label="Obrig." />
          </Grid>
          <Grid item xs={2} sm={1}>
            <IconButton size="small" onClick={() => onMudar(colunas.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Grid>
        </Grid>
      ))}
      <Button size="small" startIcon={<AddIcon />}
        onClick={() => onMudar([...colunas, { id: Math.random().toString(16).slice(2, 8), titulo: "", tipo: "texto", opcoes: [] }])}>
        Adicionar coluna
      </Button>
    </div>
  );
};

// "Mostrar só quando": a pergunta aparece (e é exigida) só com certas respostas de uma anterior
const CondicaoEditor = ({ pergunta, anteriores, onMudar }) => {
  const refs = anteriores.filter((q) => ["escolha", "lista", "caixas", "verificacao"].includes(q.tipo) && opcoesDe(q).length);
  if (!refs.length) return null;
  const c = pergunta.condicao || null;
  const ref = refs.find((q) => q.id === c?.pergunta);
  return (
    <>
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" variant="outlined" label="Mostrar só quando…"
          value={c?.pergunta || ""} onChange={(e) => onMudar(e.target.value ? { pergunta: e.target.value, valores: [] } : null)}>
          <MenuItem value="">Sempre mostrar</MenuItem>
          {refs.map((q) => <MenuItem key={q.id} value={q.id}>{q.titulo || "(sem título)"}</MenuItem>)}
        </TextField>
      </Grid>
      {ref && (
        <Grid item xs={12} sm={6}>
          {opcoesDe(ref).filter((o) => o.trim()).map((o) => (
            <FormControlLabel key={o} label={o} control={
              <Checkbox size="small" color="primary" checked={(c.valores || []).includes(o)}
                onChange={(e) => onMudar({ ...c, valores: e.target.checked ? [...(c.valores || []), o] : (c.valores || []).filter((x) => x !== o) })} />
            } />
          ))}
        </Grid>
      )}
    </>
  );
};

export const novaPergunta = (tipo = "texto") => ({
  id: Math.random().toString(16).slice(2, 10), titulo: "", tipo, opcoes: [], obrigatoria: false, ajuda: "", mapear: null,
  ...(tipo === "tabela" ? { colunas: [] } : {}),
});
const ROTULO_TIPO = Object.fromEntries([...TIPOS, ...TIPOS_CHECKLIST]);
const ACEITAR = [["fotos", "Fotos"], ["documentos", "Documentos (PDF, Word) ou fotos"], ["video", "Vídeo (até 200 MB)"]];
const COM_OPCOES = ["escolha", "caixas", "lista"];

// Como o técnico vê o formulário (só visual; nada é gravado)
export const PreviaPergunta = ({ p, todas }) => {
  const c = useStyles();
  if (p.tipo === "secao") return <Typography className={c.secao} style={{ fontSize: 16, marginTop: 18 }}>{p.titulo || "Seção sem título"}</Typography>;
  const opcoes = (p.opcoes || []).filter((o) => o.trim());
  const ref = p.condicao && todas.find((q) => q.id === p.condicao.pergunta);
  let campo;
  if (p.tipo === "paragrafo") campo = <TextField fullWidth multiline rows={3} variant="outlined" size="small" />;
  else if (p.tipo === "escolha") campo = <RadioGroup>{opcoes.map((o) => <FormControlLabel key={o} value={o} control={<Radio size="small" color="primary" />} label={o} />)}</RadioGroup>;
  else if (p.tipo === "caixas") campo = opcoes.map((o) => <div key={o}><FormControlLabel control={<Checkbox size="small" color="primary" />} label={o} /></div>);
  else if (p.tipo === "lista") campo = <TextField select fullWidth variant="outlined" size="small" value="">{opcoes.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField>;
  else if (p.tipo === "telefone") campo = <TextField fullWidth variant="outlined" size="small" placeholder="11999998888"
    helperText="DDD + número, sem o 0 · teclado numérico" />;
  else if (p.tipo === "senha") campo = <TextField fullWidth variant="outlined" size="small" type="password" placeholder="••••"
    helperText={`Teclado numérico · ${p.min_digitos || 4} a ${p.max_digitos || 8} dígitos · 👁 para ver${p.confirma ? " · confirmação" : ""}`} />;
  else if (p.tipo === "verificacao") campo = <RadioGroup row>{OPCOES_VERIFICACAO.map((o) => <FormControlLabel key={o} value={o} control={<Radio size="small" color="primary" />} label={o} />)}</RadioGroup>;
  else if (p.tipo === "assinatura") campo = (
    <div style={{ height: 110, border: "1px dashed #999", borderRadius: 8, display: "flex", alignItems: "flex-end", padding: 8 }}>
      <Typography variant="caption" color="textSecondary">Assine com o dedo ou o mouse</Typography>
    </div>
  );
  else if (p.tipo === "foto") campo = <Button variant="outlined" color="primary">{{ documentos: "📄 Anexar arquivo", video: "🎥 Enviar vídeo" }[p.aceitar] || "📷 Adicionar fotos"}</Button>;
  else if (p.tipo === "tabela") {
    campo = (
      <Paper variant="outlined" style={{ padding: 8 }}>
        <Typography variant="caption" color="textSecondary">{`Cada linha: ${(p.colunas || []).map((x) => x.titulo || "?").join(" · ") || "sem colunas"}`}</Typography>
        <div><Button size="small" startIcon={<AddIcon />} color="primary">Adicionar linha</Button></div>
      </Paper>
    );
  } else {
    const tipo = { data: "date", hora: "time", numero: "number" }[p.tipo] || "text";
    campo = <TextField fullWidth variant="outlined" size="small" type={tipo} InputLabelProps={{ shrink: true }}
      placeholder={p.tipo === "iccid" ? "89 55 … (20 dígitos)" : ""} />;
  }
  return (
    <div style={{ margin: "14px 0" }}>
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        {p.titulo || "(pergunta sem título)"}{p.obrigatoria && <span style={{ color: "#d32f2f" }}> *</span>}
      </Typography>
      {p.ajuda && <Typography variant="caption" color="textSecondary" component="div">{p.ajuda}</Typography>}
      {ref && (
        <Typography variant="caption" style={{ color: "#ef6c00" }} component="div">
          {`Aparece só quando “${ref.titulo}” = ${(p.condicao.valores || []).join(" ou ") || "?"}`}
        </Typography>
      )}
      <div style={{ marginTop: 4 }}>{campo}</div>
    </div>
  );
};

const PreviaFormulario = ({ f }) => (
  <Paper variant="outlined" style={{ maxWidth: 460, margin: "0 auto", padding: 18, borderRadius: 14 }}>
    <Typography variant="h6">{f.nome || "Formulário sem nome"}</Typography>
    {f.descricao && <Typography variant="body2" color="textSecondary" style={{ whiteSpace: "pre-wrap" }}>{f.descricao}</Typography>}
    {f.perguntas.map((p) => <PreviaPergunta key={p.id} p={p} todas={f.perguntas} />)}
    <Button variant="contained" color="primary" fullWidth style={{ marginTop: 12 }}>Enviar respostas</Button>
  </Paper>
);

// Edição da pergunta selecionada (checklist: tipos do checklist, sem "preenche o card" nem opções de anexo)
export const EditorPergunta = ({ p, i, total, anteriores, mudar, mover, duplicar, remover, mapear = MAPEAR, listas = [],
  tipos = TIPOS, checklist = false }) => (
  <Paper variant="outlined" style={{ padding: 16 }}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
      <Typography variant="subtitle2" style={{ flex: 1 }}>{`${p.tipo === "secao" ? "Seção" : "Pergunta"} ${i + 1} de ${total}`}</Typography>
      <Tooltip title="Subir"><span><IconButton size="small" disabled={i === 0} onClick={() => mover(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
      <Tooltip title="Descer"><span><IconButton size="small" disabled={i === total - 1} onClick={() => mover(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
      <Tooltip title="Duplicar"><IconButton size="small" onClick={() => duplicar(i)}><FileCopyOutlined fontSize="small" /></IconButton></Tooltip>
      <Tooltip title="Remover"><IconButton size="small" onClick={() => remover(i)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
    </div>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={8}>
        <TextField fullWidth autoFocus variant="outlined" label={p.tipo === "secao" ? "Título da seção" : "Pergunta"}
          value={p.titulo} onChange={(e) => mudar(i, { titulo: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField select fullWidth variant="outlined" label="Tipo de resposta" value={p.tipo}
          onChange={(e) => mudar(i, { tipo: e.target.value, ...(e.target.value === "tabela" && !p.colunas ? { colunas: [] } : {}) })}>
          {tipos.map(([v, r]) => <MenuItem key={v} value={v}>{r}</MenuItem>)}
        </TextField>
      </Grid>
      {checklist && p.tipo === "verificacao" && (
        <Grid item xs={12}>
          <FormControlLabel control={<Switch color="primary" checked={!!p.exige_foto} onChange={(e) => mudar(i, { exige_foto: e.target.checked })} />}
            label="Exige foto (conforme ou não conforme)" />
          <Typography variant="caption" color="textSecondary" component="div">
            “Não conforme” abre um apontamento; “Não se aplica” e “Não verificado” pedem justificativa.
          </Typography>
        </Grid>
      )}
      {COM_OPCOES.includes(p.tipo) && !checklist && (
        <Grid item xs={12} sm={5}>
          <TextField select fullWidth variant="outlined" size="small" label="Opções" value={p.lista || ""}
            onChange={(e) => mudar(i, { lista: e.target.value || null })}
            helperText={p.lista ? "Sempre as opções ativas da lista (mude em Listas)" : "Escreva as opções ao lado"}>
            <MenuItem value="">Digitar as opções</MenuItem>
            {listas.map((l) => <MenuItem key={l.categoria} value={l.categoria}>{`Lista: ${l.nome}`}</MenuItem>)}
          </TextField>
        </Grid>
      )}
      {COM_OPCOES.includes(p.tipo) && !p.lista && (
        <Grid item xs={12} sm={checklist ? 12 : 7}>
          <TextField fullWidth variant="outlined" multiline rows={4} label="Opções (uma por linha)"
            value={(p.opcoes || []).join("\n")} onChange={(e) => mudar(i, { opcoes: e.target.value.split("\n") })} />
        </Grid>
      )}
      {COM_OPCOES.includes(p.tipo) && p.lista && (
        <Grid item xs={12} sm={7}>
          {(p.opcoes || []).map((o) => <Chip key={o} size="small" label={o} style={{ margin: 2 }} />)}
          {!(p.opcoes || []).length && <Typography variant="caption" color="textSecondary">As opções aparecem ao salvar.</Typography>}
        </Grid>
      )}
      {p.tipo === "tabela" && (
        <Grid item xs={12}><ColunasEditor colunas={p.colunas || []} onMudar={(colunas) => mudar(i, { colunas })} /></Grid>
      )}
      {p.tipo === "senha" && (
        <>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth variant="outlined" size="small" type="number" label="Mínimo de dígitos" value={p.min_digitos ?? 4}
              onChange={(e) => mudar(i, { min_digitos: Number(e.target.value) || 1 })} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth variant="outlined" size="small" type="number" label="Máximo de dígitos" value={p.max_digitos ?? 6}
              onChange={(e) => mudar(i, { max_digitos: Number(e.target.value) || 1 })} helperText="Conforme o painel de alarme" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth variant="outlined" size="small" label="Esta pergunta é" value={p.confirma || ""}
              onChange={(e) => mudar(i, { confirma: e.target.value || null })}
              helperText={p.confirma ? "Tem de ser igual à outra; não é guardada" : "Guardada cifrada; quem vê o formulário revela (fica registrado)"}>
              <MenuItem value="">A senha</MenuItem>
              {anteriores.filter((q) => q.tipo === "senha" && !q.confirma).map((q) => (
                <MenuItem key={q.id} value={q.id}>{`Confirmação de “${q.titulo || "senha"}”`}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </>
      )}
      {p.tipo === "foto" && !checklist && (
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth variant="outlined" size="small" label="Aceita" value={p.aceitar || "fotos"}
            onChange={(e) => mudar(i, { aceitar: e.target.value })}>
            {ACEITAR.map(([v, r]) => <MenuItem key={v} value={v}>{r}</MenuItem>)}
          </TextField>
        </Grid>
      )}
      {p.tipo === "foto" && !checklist && (
        <>
          <Grid item xs={12} sm={6}>
            <FormControlLabel control={<Switch color="primary" checked={!!p.validade}
              onChange={(e) => mudar(i, { validade: e.target.checked })} />}
              label="Pedir data de validade (controle de documentos)" />
          </Grid>
          {p.validade && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth variant="outlined" size="small" label="Tipo de documento" value={p.documento || ""}
                placeholder="Ex.: ASO, NR-10, NR-35, INTEGRAÇÃO" onChange={(e) => mudar(i, { documento: e.target.value })}
                helperText="Nome no controle de documentos (vazio = título da pergunta)" />
            </Grid>
          )}
        </>
      )}
      {p.tipo !== "secao" && (
        <>
          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" size="small" label={checklist ? "Texto de ajuda (opcional)" : "Texto de ajuda para o técnico (opcional)"} value={p.ajuda || ""}
              onChange={(e) => mudar(i, { ajuda: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel control={<Switch color="primary" checked={!!p.obrigatoria}
              onChange={(e) => mudar(i, { obrigatoria: e.target.checked })} />} label="Resposta obrigatória" />
          </Grid>
          {!checklist && (
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth variant="outlined" size="small" label="Ao validar, preenche…"
                value={p.mapear || ""} onChange={(e) => mudar(i, { mapear: e.target.value || null })}
                helperText="Leva a resposta para o card ou para o cadastro do cliente">
                {mapear.map(([v, r]) => <MenuItem key={v} value={v}>{r}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <CondicaoEditor pergunta={p} anteriores={anteriores} onMudar={(condicao) => mudar(i, { condicao })} />
        </>
      )}
    </Grid>
  </Paper>
);

const EditorFormulario = ({ modelo, filas, modelosCard = [], onClose, onSalvo }) => {
  const c = useStyles();
  const [f, setF] = useState(null);
  const [sel, setSel] = useState(0);
  const [vista, setVista] = useState("editar");
  const [sujo, setSujo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [menuTipo, setMenuTipo] = useState(null);
  const [listas, setListas] = useState([]);
  useEffect(() => {
    suporteApi.get("/api/listas-config").then((r) => setListas(r.data.filter((l) => l.ativo))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!modelo) return;
    setF(JSON.parse(JSON.stringify(modelo)));
    setSel(0);
    setVista("editar");
    setSujo(false);
  }, [modelo]);
  if (!f) return null;

  const alterar = (fn) => { setSujo(true); setF(fn); };
  const mudar = (dados) => alterar((s) => ({ ...s, ...dados }));
  const mudarP = (i, dados) => alterar((s) => ({ ...s, perguntas: s.perguntas.map((p, j) => (j === i ? { ...p, ...dados } : p)) }));
  const mover = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= f.perguntas.length) return;
    alterar((s) => { const ps = [...s.perguntas]; [ps[i], ps[j]] = [ps[j], ps[i]]; return { ...s, perguntas: ps }; });
    setSel(j);
  };
  const inserir = (p) => {
    const pos = f.perguntas.length ? Math.min(sel + 1, f.perguntas.length) : 0;
    alterar((s) => { const ps = [...s.perguntas]; ps.splice(pos, 0, p); return { ...s, perguntas: ps }; });
    setSel(pos);
    setVista("editar");
  };
  const duplicar = (i) => inserir({ ...JSON.parse(JSON.stringify(f.perguntas[i])), id: novaPergunta().id, titulo: `${f.perguntas[i].titulo} (cópia)` });
  const remover = (i) => {
    alterar((s) => ({ ...s, perguntas: s.perguntas.filter((_, j) => j !== i) }));
    setSel(Math.max(0, i - 1));
  };
  const fechar = () => {
    if (sujo && !window.confirm("Sair sem salvar as alterações?")) return;
    onClose();
  };
  const salvar = async () => {
    if (!(f.nome || "").trim()) return toast.error("Dê um nome ao formulário");
    const vazia = f.perguntas.findIndex((p) => !(p.titulo || "").trim());
    if (vazia >= 0) {
      setSel(vazia);
      setVista("editar");
      return toast.error(`A pergunta ${vazia + 1} está sem título`);
    }
    setSalvando(true);
    try {
      const corpo = { nome: f.nome, descricao: f.descricao || "", ativo: f.ativo !== false, perguntas: f.perguntas,
        preencher_ao_receber: !!f.preencher_ao_receber, filas: f.filas || [], exige_qra: f.exige_qra !== false,
        por_etapas: !!f.por_etapas, texto_enviar: f.texto_enviar || "", mensagem_final: f.mensagem_final || "",
        whatsapp_pergunta: f.whatsapp_pergunta || null, rotulos: f.rotulos || {},
        enviar_ao_abrir: !!f.enviar_ao_abrir, validade_horas: Number(f.validade_horas) || null,
        resposta_rapida_ativa: f.resposta_rapida_ativa !== false };
      if (f.id) await suporteApi.put(`/api/formularios/${f.id}`, corpo);
      else await suporteApi.post("/api/formularios", corpo);
      toast.success("Formulário salvo");
      setSujo(false);
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setSalvando(false);
  };
  const p = f.perguntas[sel];
  // campos dos cards das filas deste formulário (sem fila marcada: de todas)
  const opcoesMapear = [...MAPEAR, ...modelosCard
    .filter((mc) => mc.fila_id === 0 || !(f.filas || []).length || f.filas.includes(mc.fila_id))
    .flatMap((mc) => mc.campos.map((cp) => [`card:${cp.id}`, `Card ${mc.nome}: ${cp.rotulo}`]))];

  return (
    <Dialog open fullScreen onClose={fechar}>
      <div className={c.editorTopo}>
        <IconButton onClick={fechar}><CloseIcon /></IconButton>
        <TextField placeholder="Nome do formulário" value={f.nome || ""} onChange={(e) => mudar({ nome: e.target.value })}
          InputProps={{ style: { fontSize: 20, fontWeight: 600 } }} style={{ flex: "1 1 260px" }} />
        {sujo && <Chip size="small" label="Alterações não salvas" style={{ background: "#fff3e0", color: "#8a4b00" }} />}
        <Tabs value={vista} onChange={(e, v) => setVista(v)} indicatorColor="primary" textColor="primary">
          <Tab value="editar" label="Editar" />
          <Tab value="previa" label="Ver como o técnico" />
        </Tabs>
        <Button color="primary" variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando…" : "Salvar"}</Button>
      </div>
      <div className={c.editorCorpo}>
        {vista === "previa" ? (
          <PreviaFormulario f={f} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                <TextField fullWidth multiline variant="outlined" size="small" label="Descrição (aparece no topo para o técnico)"
                  value={f.descricao || ""} onChange={(e) => mudar({ descricao: e.target.value })} />
                <FormControlLabel control={<Switch color="primary" checked={f.ativo !== false} onChange={(e) => mudar({ ativo: e.target.checked })} />}
                  label="Ativo (pode ser enviado)" />
                <FormControlLabel control={<Switch color="primary" checked={!!f.preencher_ao_receber} onChange={(e) => mudar({ preencher_ao_receber: e.target.checked })} />}
                  label="Preencher o card assim que o técnico responder" />
                <FormControlLabel control={<Switch color="primary" checked={f.exige_qra === false} onChange={(e) => mudar({ exige_qra: !e.target.checked })} />}
                  label="Link aberto: qualquer pessoa responde, sem QRA e sem card (ex.: candidatos)" />
                <FormControlLabel control={<Switch color="primary" checked={!!f.por_etapas} onChange={(e) => mudar({ por_etapas: e.target.checked })} />}
                  label="Em etapas: uma seção por página, com Voltar / Continuar" />
              </Paper>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                <Typography variant="subtitle2">Envio do link</Typography>
                <FormControlLabel
                  control={<Checkbox color="primary" size="small" checked={!!f.enviar_ao_abrir}
                    onChange={(e) => mudar({ enviar_ao_abrir: e.target.checked })} />}
                  label="Enviar automaticamente ao abrir Chamado técnico pela conversa" />
                <TextField variant="outlined" size="small" margin="dense" type="number" label="Validade do link (horas)"
                  value={f.validade_horas || ""} onChange={(e) => mudar({ validade_horas: e.target.value ? Number(e.target.value) : null })}
                  helperText="Vazio = sem prazo. Expirado, use “Reenviar link” no card (gera um link novo)." style={{ maxWidth: 320 }} />
                <FormControlLabel
                  control={<Checkbox color="primary" size="small" checked={f.resposta_rapida_ativa !== false}
                    onChange={(e) => mudar({ resposta_rapida_ativa: e.target.checked })} />}
                  label="Criar resposta rápida com link fixo" />
                <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
                  Link fixo: o técnico informa o QRA; sem card dele, a resposta fica aguardando o atendente (não abre card).
                </Typography>
                <Typography variant="subtitle2">Ao responder</Typography>
                <TextField fullWidth variant="outlined" size="small" margin="dense" label="Texto do botão final"
                  placeholder="Enviar" value={f.texto_enviar || ""} onChange={(e) => mudar({ texto_enviar: e.target.value })} />
                <TextField fullWidth variant="outlined" size="small" margin="dense" multiline rows={3} label="Mensagem depois de enviar (orientações)"
                  value={f.mensagem_final || ""} onChange={(e) => mudar({ mensagem_final: e.target.value })} />
                <TextField select fullWidth variant="outlined" size="small" margin="dense" label="Responder no WhatsApp informado em"
                  value={f.whatsapp_pergunta || ""} onChange={(e) => mudar({ whatsapp_pergunta: e.target.value || null })}
                  helperText="Link aberto: manda a mensagem acima (sem senha) e o aviso de validação para esse número">
                  <MenuItem value="">Não responder</MenuItem>
                  {f.perguntas.filter((q) => ["texto", "numero", "telefone"].includes(q.tipo)).map((q) => <MenuItem key={q.id} value={q.id}>{q.titulo || "(sem título)"}</MenuItem>)}
                </TextField>
                <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 6 }}>Nomes das situações (opcional)</Typography>
                {[["RESPONDIDO", "Recebido (no lugar de “Para validar”)"], ["VALIDADO", "Validado (no lugar de “Validado”)"], ["botao_validar", "Botão de validar"]].map(([k, rot]) => (
                  <TextField key={k} fullWidth variant="outlined" size="small" margin="dense" label={rot}
                    value={(f.rotulos || {})[k] || ""} onChange={(e) => mudar({ rotulos: { ...(f.rotulos || {}), [k]: e.target.value } })} />
                ))}
              </Paper>
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                <Typography variant="subtitle2">Quem vê no painel</Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  Marque as filas do Whaticket. Sem nenhuma marcada, todos veem. Administradores sempre veem.
                </Typography>
                {filas.map((q) => (
                  <FormControlLabel key={q.id} label={q.nome} control={
                    <Checkbox color="primary" size="small" checked={(f.filas || []).includes(q.id)}
                      onChange={(e) => mudar({ filas: e.target.checked ? [...(f.filas || []), q.id] : (f.filas || []).filter((x) => x !== q.id) })} />
                  } />
                ))}
                {!!(f.filas || []).length && (
                  <Typography variant="caption" style={{ color: "#ef6c00" }} component="div">
                    A resposta rápida deste formulário no Whaticket também fica só nessas filas.
                  </Typography>
                )}
              </Paper>
              <Paper variant="outlined">
                <Typography variant="subtitle2" style={{ padding: "10px 12px 0" }}>{`Perguntas (${f.perguntas.length})`}</Typography>
                <List dense>
                  {f.perguntas.map((q, i) => (
                    <ListItem key={q.id} button selected={i === sel} onClick={() => { setSel(i); }}
                      style={q.tipo === "secao" ? { background: "rgba(63,81,181,0.06)" } : undefined}>
                      <ListItemText
                        primary={<span style={{ fontWeight: q.tipo === "secao" ? 700 : 400 }}>{`${i + 1}. ${q.titulo || "(sem título)"}`}{q.obrigatoria ? " *" : ""}</span>}
                        secondary={[ROTULO_TIPO[q.tipo], q.mapear && `→ ${(opcoesMapear.find(([v]) => v === q.mapear) || [])[1] || q.mapear}`, q.condicao && "condicional"].filter(Boolean).join(" · ")}
                      />
                    </ListItem>
                  ))}
                </List>
                <div style={{ padding: "0 8px 8px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Button size="small" color="primary" startIcon={<AddIcon />} onClick={(e) => setMenuTipo(e.currentTarget)}>Pergunta</Button>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => inserir(novaPergunta("secao"))}>Seção</Button>
                </div>
                <Menu anchorEl={menuTipo} open={!!menuTipo} onClose={() => setMenuTipo(null)}>
                  {TIPOS.filter(([v]) => v !== "secao").map(([v, r]) => (
                    <MenuItem key={v} onClick={() => { setMenuTipo(null); inserir(novaPergunta(v)); }}>{r}</MenuItem>
                  ))}
                </Menu>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              {p ? (
                <EditorPergunta p={p} i={sel} total={f.perguntas.length} anteriores={f.perguntas.slice(0, sel)}
                  mudar={mudarP} mover={mover} duplicar={duplicar} remover={remover} mapear={opcoesMapear} listas={listas} />
              ) : (
                <Paper variant="outlined" style={{ padding: 24, textAlign: "center" }}>
                  <Typography color="textSecondary">Nenhuma pergunta ainda. Use “+ Pergunta” para começar.</Typography>
                </Paper>
              )}
              {p && p.tipo !== "secao" && (
                <Paper variant="outlined" style={{ padding: 12, marginTop: 12 }}>
                  <Typography variant="caption" color="textSecondary">Prévia desta pergunta</Typography>
                  <PreviaPergunta p={p} todas={f.perguntas} />
                </Paper>
              )}
            </Grid>
          </Grid>
        )}
      </div>
    </Dialog>
  );
};

// ------------------------------------------------------------------ relatório
const hojeISO = () => new Date().toISOString().slice(0, 10);
const diasAtrasISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const minutos = (m) => {
  if (m === null || m === undefined) return "-";
  const v = Number(m);
  if (v < 60) return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} min`;
  if (v < 1440) return `${Math.floor(v / 60)}h${String(Math.round(v % 60)).padStart(2, "0")}`;
  return `${Math.floor(v / 1440)}d ${Math.floor((v % 1440) / 60)}h`;
};

const TabelaSimples = ({ titulo, cabecalho, linhas, vazio = "Sem dados no período." }) => (
  <Paper variant="outlined" style={{ padding: 12, marginTop: 12, overflowX: "auto" }}>
    <Typography variant="subtitle1"><b>{titulo}</b></Typography>
    <Table size="small">
      <TableHead>
        <TableRow>{cabecalho.map(([t, al]) => <TableCell key={t} align={al}>{t}</TableCell>)}</TableRow>
      </TableHead>
      <TableBody>
        {linhas.length === 0 && (
          <TableRow><TableCell colSpan={cabecalho.length}><Typography variant="body2" color="textSecondary">{vazio}</Typography></TableCell></TableRow>
        )}
        {linhas.map((l, i) => (
          <TableRow key={i} hover>{l.map((v, j) => <TableCell key={j} align={cabecalho[j][1]}>{v}</TableCell>)}</TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const escHtml = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Uma resposta em HTML (para impressão / PDF)
const htmlResposta = (e) => {
  const base = (e.link || "").split("/f/")[0];
  const token = e.token || (e.link || "").split("/f/")[1];
  const imgs = (lista) => (lista || []).map((n) => `<img src="${base}/f/${token}/arquivos/${encodeURIComponent(n)}">`).join("");
  const r = e.respostas || {};
  let corpo = "";
  for (const p of e.perguntas) {
    if (p.tipo === "secao") { corpo += `<h2>${escHtml(p.titulo)}</h2>`; continue; }
    const v = r[p.id];
    let valor;
    if (p.tipo === "tabela") {
      const cols = p.colunas || [];
      valor = `<table><thead><tr>${cols.map((c) => `<th>${escHtml(c.titulo)}</th>`).join("")}</tr></thead><tbody>${(v || [])
        .map((l) => `<tr>${cols.map((c) => `<td>${c.tipo === "foto" ? imgs(l[c.id]) : escHtml(l[c.id] || "—")}</td>`).join("")}</tr>`)
        .join("")}</tbody></table>`;
    } else if (p.tipo === "foto" || p.tipo === "assinatura") valor = (v || []).length ? `<div class="fotos">${imgs(v)}</div>` : "—";
    else valor = escHtml(Array.isArray(v) ? v.join(", ") : v || "—");
    corpo += `<div class="q"><div class="t">${escHtml(p.titulo)}</div><div>${valor}</div></div>`;
  }
  const [rot] = STATUS_FORM[situacao(e)] || [e.status];
  const info = [
    ["Situação", rot], ["Técnico", e.qra != null ? `QRA ${e.qra} · ${e.tecnico || ""}` : "-"],
    ["Cliente", [e.conta, e.filial].filter(Boolean).join(" — ") || "-"], ["O.S", e.os || "-"],
    ["Enviado", `${dataBR(e.enviado_em)} por ${e.enviado_por || "-"}`], ["Respondido", dataBR(e.respondido_em)],
    ["Validação", e.validado_em ? `${dataBR(e.validado_em)} por ${e.validado_por}${e.comentario ? ` — ${e.comentario}` : ""}` : "-"],
  ].map(([k, v]) => `<tr><th>${k}</th><td>${escHtml(v)}</td></tr>`).join("");
  return `<section><h1>${escHtml(e.nome_formulario)}</h1><div class="sub">Suporte Allarmi · resposta nº ${e.id}</div>
    <table class="info">${info}</table>${corpo}</section>`;
};

// Abre uma página com as respostas (uma por página) e chama a impressão / "Salvar como PDF"
export const imprimirRespostas = (lista) => {
  if (!lista.length) return;
  const w = window.open("", "_blank");
  if (!w) return toast.error("Libere as janelas pop-up para gerar o PDF");
  const titulo = lista.length === 1 ? lista[0].nome_formulario : `${lista.length} respostas de formulários`;
  w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escHtml(titulo)}</title>
    <style>body{font:14px/1.4 system-ui,Segoe UI,Roboto,sans-serif;color:#1d2330;margin:24px}h1{font-size:20px;margin:0 0 2px}
    .sub{color:#667;margin-bottom:8px}h2{font-size:15px;color:#3f51b5;border-bottom:1px solid #ccd;padding-bottom:2px;margin:18px 0 6px}
    .q{margin:8px 0;page-break-inside:avoid}.t{font-weight:600;color:#555;font-size:12px}
    table{border-collapse:collapse;width:100%;margin-top:4px}th,td{border:1px solid #ccd;padding:4px 6px;text-align:left;vertical-align:top;font-size:12px}
    th{background:#f0f2f7}.info th{width:110px}img{width:160px;height:120px;object-fit:cover;margin:2px;border:1px solid #ccd;border-radius:4px}
    td img{width:90px;height:68px}.fotos{display:flex;flex-wrap:wrap}section+section{page-break-before:always;margin-top:24px}
    @media print{body{margin:10mm}}</style></head>
    <body>${lista.map(htmlResposta).join("")}
    <script>window.onload=function(){setTimeout(function(){window.print()},500)}<\/script></body></html>`);
  w.document.close();
};

export const imprimirResposta = (e) => imprimirRespostas([e]);

export const DetalheResposta = ({ id, onClose, onAbrirCard, onMudou }) => {
  const [e, setE] = useState(null);
  const carregar = useCallback(() => {
    suporteApi.get(`/api/formularios/envios/${id}`).then((r) => setE(r.data)).catch((err) => toast.error(mensagemErro(err)));
  }, [id]);
  useEffect(() => {
    if (id) carregar();
    else setE(null);
  }, [id, carregar]);
  const [rot, cor] = e ? STATUS_FORM[situacao(e)] || [e.status, "default"] : [];
  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{e ? e.nome_formulario : "Carregando…"}</DialogTitle>
      <DialogContent dividers>
        {e && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
              <Chip size="small" label={rot} color={cor} />
              <Typography variant="body2">
                {[e.qra != null && `QRA ${e.qra} · ${e.tecnico || ""}`, [e.conta, e.filial].filter(Boolean).join(" — "), e.os && `O.S ${e.os}`]
                  .filter(Boolean).join(" · ")}
              </Typography>
            </div>
            <Typography variant="caption" color="textSecondary" component="div">
              {`Enviado ${dataBR(e.enviado_em)} por ${e.enviado_por || "-"} · respondido ${dataBR(e.respondido_em)}`}
              {e.validado_em ? ` · ${e.status === "VALIDADO" ? "validado" : "revisado"} por ${e.validado_por} em ${dataBR(e.validado_em)}` : ""}
              {e.correcoes ? ` · ${e.correcoes} correção(ões) pedida(s)` : ""}
            </Typography>
            <Divider style={{ margin: "10px 0" }} />
            <RespostasEnvio envio={e} onMudou={() => { carregar(); onMudou && onMudou(); }} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {e && <Button onClick={() => imprimirResposta(e)}>Imprimir / PDF</Button>}
        {e?.atendimento_id && <Button onClick={() => onAbrirCard(e.atendimento_id)}>Abrir card</Button>}
        {e && <Button onClick={() => copiar(e.link)}>Copiar link</Button>}
        <Button color="primary" onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

const RelatorioFormularios = ({ onAbrirCard }) => {
  const [modelos, setModelos] = useState([]);
  const [f, setF] = useState({ formulario_id: "", de: diasAtrasISO(29), ate: hojeISO(), status: "", busca: "" });
  const [r, setR] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [marcados, setMarcados] = useState([]);
  const [gerando, setGerando] = useState(false);
  const params = (x = f) => Object.fromEntries(Object.entries(x).filter(([, v]) => v));
  const gerar = async (x = f, primeira = false) => {
    try {
      const { data } = await suporteApi.get("/api/relatorios/formularios", { params: params(x) });
      if (primeira && !x.formulario_id && data.por_formulario.length) {
        // abre direto nas respostas do formulário com mais envios
        const novo = { ...x, formulario_id: data.por_formulario[0].formulario_id };
        setF(novo);
        return gerar(novo);
      }
      setR(data);
      setMarcados([]);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  // exporta as marcadas; sem nenhuma marcada, todas as respostas da tabela
  const alvo = () => (marcados.length ? marcados : (r?.envios || []).map((e) => e.id));
  const exportarExcel = () =>
    baixar("/api/relatorios/formularios.xlsx", { ids: alvo().join(",") }).catch((err) => toast.error(mensagemErro(err)));
  const exportarPdf = async () => {
    const ids = alvo();
    if (!ids.length) return;
    setGerando(true);
    try {
      const { data } = await suporteApi.get("/api/formularios/envios", { params: { ids: ids.join(",") } });
      const ordem = new Map(ids.map((id, i) => [id, i]));
      imprimirRespostas([...data].sort((a, b) => ordem.get(a.id) - ordem.get(b.id)));
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setGerando(false);
  };
  const alternar = (id) => setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  useEffect(() => {
    suporteApi.get("/api/formularios").then((x) => setModelos(x.data)).catch(() => {});
    gerar(f, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const campo = (nome) => ({ value: f[nome], onChange: (e) => setF({ ...f, [nome]: e.target.value }), size: "small" });
  const n = (v) => (v ?? 0).toLocaleString("pt-BR");
  const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "-");

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <TextField select label="Formulário" style={{ minWidth: 260 }} {...campo("formulario_id")}>
          <MenuItem value="">Todos (resumo)</MenuItem>
          {modelos.map((m) => <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>)}
        </TextField>
        <TextField label="De" type="date" InputLabelProps={{ shrink: true }} {...campo("de")} />
        <TextField label="Até" type="date" InputLabelProps={{ shrink: true }} {...campo("ate")} />
        <TextField select label="Situação" style={{ minWidth: 150 }} {...campo("status")}>
          <MenuItem value="">Todas</MenuItem>
          {Object.entries(STATUS_FORM).map(([k, [rot]]) => <MenuItem key={k} value={k}>{rot}</MenuItem>)}
        </TextField>
        <TextField label="Buscar" placeholder="conta, filial, técnico, QRA, O.S, resposta…" style={{ minWidth: 240 }}
          {...campo("busca")} onKeyDown={(e) => e.key === "Enter" && gerar()} />
        <Button variant="contained" color="primary" onClick={() => gerar()}>Gerar</Button>
        <Button startIcon={<GetAppIcon />}
          onClick={() => baixar("/api/relatorios/formularios.xlsx", params()).catch((err) => toast.error(mensagemErro(err)))}>
          Excel (todas as respostas)
        </Button>
      </div>
      {r && (
        <>
          <TabelaSimples
            titulo="Resumo por formulário"
            cabecalho={[["Formulário"], ["Enviados", "right"], ["Respondidos", "right"], ["Taxa de resposta", "right"],
              ["Validados", "right"], ["Para validar", "right"], ["Em correção", "right"], ["Aguardando técnico", "right"],
              ["Tempo até responder", "right"], ["Tempo até validar", "right"]]}
            linhas={r.por_formulario.map((x) => [x.formulario, n(x.enviados), n(x.respondidos), pct(x.respondidos, x.enviados),
              n(x.validados), n(x.para_validar), n(x.em_correcao), n(x.aguardando),
              minutos(x.tempo_resposta_min), minutos(x.tempo_validacao_min)])}
          />
          <TabelaSimples
            titulo="Por técnico"
            cabecalho={[["#"], ["Técnico"], ["Enviados", "right"], ["Respondidos", "right"], ["Validados", "right"],
              ["Correções pedidas", "right"], ["Sem resposta", "right"], ["Tempo até responder", "right"]]}
            linhas={r.por_tecnico.map((x, i) => [i + 1, `QRA ${x.qra} · ${x.tecnico || "-"}`, n(x.enviados), n(x.respondidos),
              n(x.validados), n(x.correcoes), n(x.sem_resposta), minutos(x.tempo_resposta_min)])}
          />
          <TabelaSimples
            titulo="Por validador"
            cabecalho={[["Validador"], ["Validados", "right"], ["Correções pedidas", "right"], ["Tempo até validar", "right"]]}
            linhas={r.por_validador.map((x) => [x.validador, n(x.validados), n(x.correcoes), minutos(x.tempo_validacao_min)])}
          />
          <Paper variant="outlined" style={{ padding: 12, marginTop: 12, overflowX: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Typography variant="subtitle1" style={{ flex: 1 }}>
                <b>{`Respostas (${r.envios.length})`}</b>
                <Typography variant="caption" color="textSecondary" component="span">
                  {"  ·  marque as respostas para exportar; clique na linha para ver a resposta completa"}
                </Typography>
              </Typography>
              <Button size="small" variant="outlined" color="primary" startIcon={<GetAppIcon />}
                disabled={!r.envios.length} onClick={exportarExcel}>
                {`Excel (${marcados.length || `todas ${r.envios.length}`})`}
              </Button>
              <Button size="small" variant="contained" color="primary" disabled={!r.envios.length || gerando} onClick={exportarPdf}>
                {gerando ? "Gerando PDF…" : `PDF (${marcados.length || `todas ${r.envios.length}`})`}
              </Button>
            </div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox color="primary" size="small"
                      checked={!!r.envios.length && marcados.length === r.envios.length}
                      indeterminate={marcados.length > 0 && marcados.length < r.envios.length}
                      onChange={(e) => setMarcados(e.target.checked ? r.envios.map((x) => x.id) : [])} />
                  </TableCell>
                  {!f.formulario_id && <TableCell>Formulário</TableCell>}
                  <TableCell>Situação</TableCell><TableCell>Respondido</TableCell><TableCell>Técnico</TableCell>
                  <TableCell>Cliente</TableCell>
                  {r.colunas.map((c) => <TableCell key={c.id}>{c.titulo}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {r.envios.length === 0 && (
                  <TableRow><TableCell colSpan={7 + r.colunas.length}><Typography variant="body2" color="textSecondary">Nenhuma resposta com esses filtros.</Typography></TableCell></TableRow>
                )}
                {r.envios.map((e) => (
                  <TableRow key={e.id} hover selected={marcados.includes(e.id)} style={{ cursor: "pointer" }} onClick={() => setDetalhe(e.id)}>
                    <TableCell padding="checkbox" onClick={(ev) => ev.stopPropagation()}>
                      <Checkbox color="primary" size="small" checked={marcados.includes(e.id)} onChange={() => alternar(e.id)} />
                    </TableCell>
                    {!f.formulario_id && <TableCell>{e.nome_formulario}</TableCell>}
                    <TableCell><Chip size="small" label={(STATUS_FORM[e.status] || [e.status])[0]} color={(STATUS_FORM[e.status] || [])[1] || "default"} /></TableCell>
                    <TableCell>{e.respondido_em ? dataBR(e.respondido_em) : "-"}</TableCell>
                    <TableCell>{e.qra !== null && e.qra !== undefined ? `QRA ${e.qra} ${e.tecnico || ""}` : "-"}</TableCell>
                    <TableCell>{[e.conta, e.filial].filter(Boolean).join(" — ") || "-"}</TableCell>
                    {r.colunas.map((c) => {
                      const v = e.valores[c.id] || "";
                      return (
                        <TableCell key={c.id} style={{ maxWidth: 280 }}>
                          {v.startsWith("http")
                            ? `${v.split(" ").length} foto(s)`
                            : v.length > 120 ? `${v.slice(0, 120)}…` : v}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <DetalheResposta id={detalhe} onClose={() => setDetalhe(null)} onMudou={() => gerar()}
            onAbrirCard={(id) => { setDetalhe(null); onAbrirCard(id); }} />
        </>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ página
const POR_PAGINA = 50;
const COR_STATUS = { RESPONDIDO: "#f50057", ENVIADO: "#9e9e9e", CORRECAO: "#ef6c00", VALIDADO: "#2e7d32", EXPIRADO: "#795548" };
const ORDEM_STATUS = ["RESPONDIDO", "ENVIADO", "CORRECAO", "EXPIRADO", "VALIDADO"];

const ChipStatus = ({ status, rotulos }) => {
  const [rot, cor] = STATUS_FORM[status] || [status, "default"];
  return <Chip size="small" label={(rotulos || {})[status] || rot} color={cor} style={{ height: 20, fontSize: 11 }} />;
};

const quemOnde = (e) => [
  !e.atendimento_id && e.identificacao,
  e.categoria === "interno" ? `Interno · ${e.titulo || ""}` : [e.conta, e.filial].filter(Boolean).join(" — "),
  e.os && `O.S ${e.os}`,
  e.qra !== null && e.qra !== undefined ? `QRA ${e.qra} ${e.tecnico || ""}`.trim() : null,
].filter(Boolean).join(" · ");

// Links fixos dos formulários ativos (os mesmos das respostas rápidas), para qualquer usuário copiar
const DialogLinks = ({ open, onClose }) => {
  const [lista, setLista] = useState([]);
  useEffect(() => {
    if (open) suporteApi.get("/api/formularios", { params: { somente_ativos: true } }).then((r) => setLista(r.data)).catch(() => {});
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Links dos formulários</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          O técnico informa o QRA e a resposta entra no card aberto dele (ou abre um card novo).
          No Whaticket, a resposta rápida com o nome do formulário manda o mesmo link.
        </Typography>
        {lista.filter((m) => m.link_fixo).map((m) => (
          <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            <Typography variant="body2" style={{ flex: 1 }}><b>{m.nome}</b></Typography>
            <Button size="small" variant="outlined" color="primary" startIcon={<FileCopyOutlined />} onClick={() => copiar(m.link_fixo)}>Copiar</Button>
            <Button size="small" href={m.link_fixo} target="_blank" rel="noopener noreferrer">Abrir</Button>
          </div>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

// Linha do tempo de uma resposta
const HistoricoEnvio = ({ e }) => {
  const passos = [
    ["Enviado", e.enviado_em, `por ${e.enviado_por || "-"}${e.whatsapp_numero ? ` · WhatsApp ${e.whatsapp_numero}` : ""}`],
    ["Respondido pelo técnico", e.respondido_em, e.qra !== null && e.qra !== undefined ? `QRA ${e.qra} ${e.tecnico || ""}` : ""],
    e.correcoes ? ["Correções pedidas", null, `${e.correcoes} vez(es)`] : null,
    e.validado_em ? [e.status === "VALIDADO" ? rotuloStatus(e, "VALIDADO") : "Correção pedida", e.validado_em, `por ${e.validado_por}${e.comentario ? ` — “${e.comentario}”` : ""}`] : null,
    ...(e.acessos || []).map((a, i) => [`Senha revelada (${i + 1})`, a.quando, `por ${a.quem}`]),
  ].filter(Boolean);
  return (
    <List dense>
      {passos.map(([titulo, quando, detalhe]) => (
        <ListItem key={titulo} style={{ borderLeft: `3px solid ${quando || titulo.startsWith("Correções") ? "#3f51b5" : "#ccc"}`, marginBottom: 6 }}>
          <ListItemText primary={<b>{titulo}</b>} secondary={[quando ? `${dataBR(quando)} (${haQuanto(quando)})` : titulo.startsWith("Correções") ? "" : "ainda não", detalhe].filter(Boolean).join(" · ")} />
        </ListItem>
      ))}
    </List>
  );
};

const AtendimentoDoEnvio = ({ id }) => {
  const [card, setCard] = useState(null);
  useEffect(() => {
    setCard(null);
    if (id) suporteApi.get(`/api/atendimentos/${id}`).then((r) => setCard(r.data)).catch((err) => toast.error(mensagemErro(err)));
  }, [id]);
  if (!id) return <Typography color="textSecondary">Resposta sem atendimento ligado.</Typography>;
  return card ? <PainelCard card={card} onAtualizado={setCard} /> : <CircularProgress size={22} />;
};

const FichaResposta = ({ item, posicao, total, onAnterior, onProximo, onClose, onMudou }) => {
  const c = useStyles();
  const [e, setE] = useState(null);
  const [aba, setAba] = useState("respostas");
  const id = item?.id;
  const carregar = useCallback(() => {
    if (!id) return;
    suporteApi.get(`/api/formularios/envios/${id}`).then((r) => setE(r.data)).catch((err) => toast.error(mensagemErro(err)));
  }, [id]);
  useEffect(() => {
    setE(null);
    carregar();
  }, [carregar]);
  useEffect(() => {
    if (!id) return undefined;
    const tecla = (ev) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (ev.key === "ArrowLeft" && onAnterior) onAnterior();
      if (ev.key === "ArrowRight" && onProximo) onProximo();
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [id, onAnterior, onProximo]);

  const mudou = () => {
    suporteApi.get(`/api/formularios/envios/${id}`).then((r) => { setE(r.data); onMudou(r.data); }).catch(() => {});
  };
  const x = e || item;

  return (
    <Drawer anchor="right" open={!!id} onClose={onClose}>
      <div className={c.ficha}>
        <div className={c.fichaTopo}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tooltip title="Anterior (←)"><span><IconButton size="small" disabled={!onAnterior} onClick={onAnterior}><ChevronLeftIcon /></IconButton></span></Tooltip>
            <Typography variant="caption" color="textSecondary">{total ? `${posicao + 1} de ${total.toLocaleString("pt-BR")}` : ""}</Typography>
            <Tooltip title="Próximo (→)"><span><IconButton size="small" disabled={!onProximo} onClick={onProximo}><ChevronRightIcon /></IconButton></span></Tooltip>
            <div style={{ flex: 1 }} />
            {e && <Button size="small" onClick={() => imprimirResposta(e)}>Imprimir / PDF</Button>}
            {e && <Button size="small" startIcon={<FileCopyOutlined />} onClick={() => copiar(e.link)}>Link</Button>}
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </div>
          {x && (
            <div style={{ padding: "4px 4px 0" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="h6">{x.nome_formulario}</Typography>
                <ChipStatus status={situacao(x)} rotulos={x.rotulos} />
              </div>
              <Typography variant="body2" color="textSecondary">{quemOnde(x) || "—"}</Typography>
            </div>
          )}
          <Tabs value={aba} onChange={(ev, v) => setAba(v)} indicatorColor="primary" textColor="primary">
            <Tab value="respostas" label={x?.status === "RESPONDIDO" ? "Respostas e validação" : "Respostas"} />
            <Tab value="atendimento" label="Atendimento" disabled={!x?.atendimento_id} />
            <Tab value="historico" label="Histórico" />
          </Tabs>
        </div>
        <div className={c.fichaCorpo}>
          {!e ? <CircularProgress size={24} /> : (
            <>
              {aba === "respostas" && (e.status === "ENVIADO"
                ? <Typography color="textSecondary">O técnico ainda não respondeu. Use “Link” para copiar e reenviar.</Typography>
                : <RespostasEnvio envio={e} onMudou={mudou} />)}
              {aba === "atendimento" && <AtendimentoDoEnvio id={e.atendimento_id} />}
              {aba === "historico" && <HistoricoEnvio e={e} />}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

const ItemResposta = ({ e, onClick }) => {
  const c = useStyles();
  const quando = e.respondido_em || e.enviado_em;
  return (
    <ListItem button onClick={onClick} className={c.item}>
      <ListItemAvatar>
        <Avatar style={{ background: COR_STATUS[e.status] || "#9e9e9e" }}><AssignmentOutlinedIcon /></Avatar>
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Typography component="span" style={{ fontWeight: 600 }}>{e.nome_formulario}</Typography>
            <ChipStatus status={situacao(e)} rotulos={e.rotulos} />
            {e.correcoes > 0 && <Chip size="small" variant="outlined" label={`${e.correcoes} correção(ões)`} style={{ height: 20, fontSize: 11 }} />}
          </div>
        }
        secondary={<div className={c.fraco}>{quemOnde(e) || "—"}</div>}
      />
      <div className={c.lado}>
        <div style={{ fontSize: 13 }}>{`${e.respondido_em ? "respondido" : "enviado"} ${haQuanto(quando)}`}</div>
        <div className={c.fraco}>
          {e.validado_em ? `${e.status === "VALIDADO" ? "validado" : "revisado"} por ${e.validado_por}` : dataBR(quando)}
        </div>
      </div>
    </ListItem>
  );
};

const ListaRespostas = ({ modelos }) => {
  const c = useStyles();
  const [busca, setBusca] = useState("");
  const [filtros, setFiltros] = useState({ busca: "", status: "", formulario_id: "" });
  const [dados, setDados] = useState({ itens: [], total: 0, contagem: {} });
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState(null);
  const fim = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.busca === busca ? f : { ...f, busca })), 350);
    return () => clearTimeout(t);
  }, [busca]);
  const params = useMemo(() => Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== "")), [filtros]);
  const carregar = useCallback(async (pag) => {
    setCarregando(true);
    try {
      const { data } = await suporteApi.get("/api/formularios/respostas", { params: { ...params, pagina: pag, por_pagina: POR_PAGINA } });
      setDados((d) => ({ ...data, itens: pag === 1 ? data.itens : [...d.itens, ...data.itens] }));
      setPagina(pag);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setCarregando(false);
  }, [params]);
  useEffect(() => {
    carregar(1);
  }, [carregar]);

  const temMais = dados.itens.length < dados.total;
  useEffect(() => {
    const alvo = fim.current;
    if (!alvo || !temMais) return undefined;
    const obs = new IntersectionObserver(([en]) => {
      if (en.isIntersecting && !carregando) carregar(pagina + 1);
    }, { rootMargin: "300px" });
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [temMais, carregando, carregar, pagina]);

  const todas = Object.values(dados.contagem || {}).reduce((a, b) => a + b, 0);
  const irPara = (i) => {
    if (i >= dados.itens.length - 3 && temMais && !carregando) carregar(pagina + 1);
    setAberto(i);
  };
  // validou/pediu correção na ficha: atualiza a linha sem recarregar a lista
  const mudou = (e) => setDados((d) => ({ ...d, itens: d.itens.map((x) => (x.id === e.id ? { ...x, ...e } : x)) }));

  return (
    <>
      <Paper variant="outlined" className={c.filtros}>
        <TextField
          className={c.busca} size="small" variant="outlined" value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por cliente, conta, O.S, técnico, QRA, validador ou texto da resposta"
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: carregando && <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>,
          }}
        />
        <TextField select size="small" variant="outlined" label="Formulário" style={{ minWidth: 220 }}
          value={filtros.formulario_id} onChange={(e) => setFiltros({ ...filtros, formulario_id: e.target.value })}>
          <MenuItem value="">Todos</MenuItem>
          {modelos.map((m) => <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>)}
        </TextField>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", width: "100%" }}>
          <Chip size="small" clickable label={`Todas (${todas})`} color={filtros.status === "" ? "primary" : "default"}
            onClick={() => setFiltros({ ...filtros, status: "" })} />
          {ORDEM_STATUS.map((s) => (
            <Chip key={s} size="small" clickable label={`${STATUS_FORM[s][0]} (${(dados.contagem || {})[s] || 0})`}
              color={filtros.status === s ? "primary" : "default"}
              style={filtros.status === s ? undefined : { borderLeft: `4px solid ${COR_STATUS[s]}` }}
              onClick={() => setFiltros({ ...filtros, status: filtros.status === s ? "" : s })} />
          ))}
        </div>
      </Paper>
      <Paper variant="outlined" className={c.lista}>
        <List disablePadding>
          {dados.itens.map((e, i) => <ItemResposta key={e.id} e={e} onClick={() => setAberto(i)} />)}
        </List>
        {!carregando && !dados.itens.length && (
          <Typography color="textSecondary" style={{ padding: 24, textAlign: "center" }}>Nenhuma resposta encontrada.</Typography>
        )}
        <div ref={fim} style={{ padding: 12, textAlign: "center" }}>
          {temMais && (carregando ? <CircularProgress size={22} /> : <Button onClick={() => carregar(pagina + 1)}>Carregar mais</Button>)}
        </div>
      </Paper>
      <FichaResposta
        item={aberto !== null ? dados.itens[aberto] : null}
        posicao={aberto ?? 0}
        total={dados.total}
        onAnterior={aberto > 0 ? () => irPara(aberto - 1) : null}
        onProximo={aberto !== null && aberto < dados.itens.length - 1 ? () => irPara(aberto + 1) : null}
        onClose={() => setAberto(null)}
        onMudou={mudou}
      />
    </>
  );
};

// Modelos (admin): cartões com uso e ações rápidas
const corpoModelo = (m, mud = {}) => ({
  nome: m.nome, descricao: m.descricao || "", ativo: m.ativo, perguntas: m.perguntas,
  preencher_ao_receber: !!m.preencher_ao_receber, filas: m.filas || [], exige_qra: m.exige_qra !== false,
  por_etapas: !!m.por_etapas, texto_enviar: m.texto_enviar || "", mensagem_final: m.mensagem_final || "",
  whatsapp_pergunta: m.whatsapp_pergunta || null, rotulos: m.rotulos || {},
  enviar_ao_abrir: !!m.enviar_ao_abrir, validade_horas: m.validade_horas || null,
  resposta_rapida_ativa: m.resposta_rapida_ativa !== false, ...mud,
});

const ListaModelos = ({ modelos, filas, onEditar, onMudou }) => {
  const nomeFila = (id) => (filas.find((q) => q.id === id) || {}).nome || `fila ${id}`;
  const alternar = async (m) => {
    try {
      await suporteApi.put(`/api/formularios/${m.id}`, corpoModelo(m, { ativo: !m.ativo }));
      toast.success(m.ativo ? "Formulário desativado" : "Formulário ativado");
      onMudou();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const duplicar = (m) => onEditar({
    ...JSON.parse(JSON.stringify(corpoModelo(m))), nome: `${m.nome} (cópia)`, ativo: false,
    filas: m.filas || [], exige_qra: m.exige_qra, por_etapas: m.por_etapas,
  });
  return (
    <Grid container spacing={2} style={{ padding: 4 }}>
      <Grid item xs={12} sm={6} md={4}>
        <Paper variant="outlined" style={{ height: "100%", minHeight: 150, display: "flex", alignItems: "center", justifyContent: "center", borderStyle: "dashed", cursor: "pointer" }}
          onClick={() => onEditar({ nome: "", descricao: "", ativo: true, perguntas: [novaPergunta()] })}>
          <Button color="primary" startIcon={<AddIcon />}>Novo formulário</Button>
        </Paper>
      </Grid>
      {modelos.map((m) => (
        <Grid item xs={12} sm={6} md={4} key={m.id}>
          <Paper variant="outlined" style={{ padding: 14, height: "100%", display: "flex", flexDirection: "column", opacity: m.ativo ? 1 : 0.7 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Typography variant="subtitle1" style={{ flex: 1, fontWeight: 600, cursor: "pointer" }} onClick={() => onEditar(m)}>{m.nome}</Typography>
              <Chip size="small" label={m.ativo ? "Ativo" : "Desativado"} color={m.ativo ? "primary" : "default"} />
            </div>
            <Typography variant="body2" color="textSecondary">
              {`${m.perguntas.filter((p) => p.tipo !== "secao").length} perguntas · ${m.enviados || 0} envio(s)`}
              {m.para_validar ? ` · ${m.para_validar} para validar` : ""}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {m.ultimo_envio ? `Último envio ${haQuanto(m.ultimo_envio)} · ` : ""}{`alterado por ${m.atualizado_por || "-"} ${haQuanto(m.atualizado_em)}`}
            </Typography>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
              {(m.filas || []).length
                ? m.filas.map((id) => <Chip key={id} size="small" variant="outlined" color="primary" label={nomeFila(id)} style={{ height: 20, fontSize: 11 }} />)
                : <Chip size="small" variant="outlined" label="Todas as filas" style={{ height: 20, fontSize: 11 }} />}
              {m.exige_qra === false && <Chip size="small" variant="outlined" label="Link aberto" style={{ height: 20, fontSize: 11 }} />}
              {m.por_etapas && <Chip size="small" variant="outlined" label="Em etapas" style={{ height: 20, fontSize: 11 }} />}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 10 }}>
              <Button size="small" color="primary" variant="outlined" startIcon={<EditIcon />} onClick={() => onEditar(m)}>Editar</Button>
              <Button size="small" onClick={() => duplicar(m)}>Duplicar</Button>
              {m.ativo && m.link_fixo && <Button size="small" onClick={() => copiar(m.link_fixo)}>Copiar link</Button>}
              <Button size="small" onClick={() => alternar(m)}>{m.ativo ? "Desativar" : "Ativar"}</Button>
            </div>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export const PaginaFormularios = () => {
  const c = useStyles();
  const { user } = useContext(AuthContext);
  const admin = usePermissao("formularios.modelos");
  const [vista, setVista] = useState("respostas");
  const [modelos, setModelos] = useState([]);
  const [editando, setEditando] = useState(null);
  const [links, setLinks] = useState(false);
  const [card, setCard] = useState(null);
  const [filas, setFilas] = useState([]);
  const [modelosCard, setModelosCard] = useState([]);
  useEffect(() => {
    if (!admin) return;
    suporteApi.get("/api/formularios/filas").then((r) => setFilas(r.data)).catch(() => {});
    suporteApi.get("/api/modelos-card").then((r) => setModelosCard(r.data)).catch(() => {});
  }, [admin]);

  const carregarModelos = useCallback(() => {
    suporteApi.get("/api/formularios").then((r) => setModelos(r.data)).catch((err) => toast.error(mensagemErro(err)));
  }, []);
  useEffect(() => {
    carregarModelos();
  }, [carregarModelos]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Formulários</Title>
        <MainHeaderButtonsWrapper>
          <Button startIcon={<LinkIcon />} onClick={() => setLinks(true)}>Links</Button>
          {admin && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />}
              onClick={() => setEditando({ nome: "", descricao: "", ativo: true, perguntas: [novaPergunta()] })}>
              Novo formulário
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Tabs value={vista} onChange={(e, v) => setVista(v)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 8 }}>
        <Tab value="respostas" label="Respostas" />
        {admin && <Tab value="modelos" label={`Modelos (${modelos.length})`} />}
        <Tab value="relatorio" label="Relatório" />
      </Tabs>
      {vista === "respostas" && <ListaRespostas modelos={modelos} />}
      {vista === "modelos" && (
        <Paper variant="outlined" className={c.papel}>
          <ListaModelos modelos={modelos} filas={filas} onEditar={setEditando} onMudou={carregarModelos} />
        </Paper>
      )}
      {vista === "relatorio" && (
        <Paper variant="outlined" className={c.papel}><RelatorioFormularios onAbrirCard={setCard} /></Paper>
      )}
      {editando && <EditorFormulario modelo={editando} filas={filas} modelosCard={modelosCard} onClose={() => setEditando(null)} onSalvo={carregarModelos} />}
      <DialogLinks open={links} onClose={() => setLinks(false)} />
      <DialogCard id={card} open={!!card} onClose={() => setCard(null)} />
    </MainContainer>
  );
};
